import { CategoryDropdown, DataQualityDropdown, getCategoryParams } from "./SupplierCards";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { H2 } from "../../../../components/text/headings";
import { CaretDown } from "@phosphor-icons/react";
import { useGetSupplierOverviewData } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import { ResponsiveLine } from "@nivo/line";
import { DataTable } from "../../../../components/DataTable";
import * as React from "react";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { hideAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import LocalSpinner from "../../../../components/LocalSpinner";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";

/**
 * Warning: Unlike elsewhere, this does not use the category, data quality, or year filters from the URL.
 * It handles year differently.
 */
const SupplierTrends = ({ filters, setFilters }) => {
  const toast = useToast();

  const [startYear, setStartYear] = useState(2021);
  const [endYear, setEndYear] = useState(2030);
  const [method, setMethod] = useState("Emission");
  const [chartData, setChartData] = useState([]);
  const [rawData, setRawData] = useState(null);

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  /**
   * Processes raw emission data and formats it for chart visualization based on selected scope and calculation method.
   * Creates three data series: actual emissions (historical), targets, and projections (future).
   */
  useEffect(() => {
    if (rawData && filters?.scope) {
      /**
       * Calculates emission values and intensity based on the selected scope
       * @param {Object} data - Yearly emission data for a specific year
       * @returns {Object} Formatted emission values with intensity calculations
       */
      const getData = (data) => {
        let emission = 0;
        let totalSpend = data?.totalSpend || 0;
        let projectedSpend = data?.projectedSpend || 0;
        let intensity = 0;
        let projectedEmission = 0;
        let projectedIntensity = 0;

        // Calculate emissions based on scope selection
        if (filters?.scope === "All Scopes") {
          emission = data?.totalEmissions || 0;
          projectedEmission = data?.projectedEmissions || 0;
        } else if (filters?.scope === "Scope 1 and 2") {
          emission = (data?.scope1Emissions || 0) + (data?.scope2Emissions || 0);
          projectedEmission = (data?.projectedScope1Emissions || 0) + (data?.projectedScope2Emissions || 0);
        } else if (filters?.scope === "Scope 3") {
          emission = data?.scope3Emissions || 0;
          projectedEmission = data?.projectedScope3Emissions || 0;
        }

        // Calculate intensity as emissions per spend (avoid division by zero)
        intensity = totalSpend ? emission / totalSpend : 0;
        projectedIntensity = projectedSpend ? projectedEmission / projectedSpend : 0;

        return {
          emission: emission,
          intensity: intensity,
          projectedEmissions: projectedEmission,
          projectedIntensity: projectedIntensity,
        };
      };

      const currentYear = new Date().getFullYear();

      const formattedData = [
        {
          id: "Supplier Emissions",
          // Only show historical data (up to current year)
          data: Object.entries(rawData)
            .filter(([year]) => parseInt(year) <= currentYear)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.intensity : getData(values)?.emission,
            })),
          color: "#63AD03",
        },
        {
          id: "Total Target",
          // Show targets for all years
          data: Object.entries(rawData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.totalIntensityTarget : values?.totalTarget,
          })),
          color: "#FF9500",
        },
        {
          id: "Scoped Target",
          // Show targets for all years
          data: Object.entries(rawData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.scopedIntensityTarget : values?.scopedEmissionTarget,
          })),
          color: "#FF9500",
        },
        {
          id: "Projected",
          // Show projections from previous year onwards (includes overlap year for continuity)
          data: Object.entries(rawData)
            // only show projections from previous year onwards + 3 years
            .filter(([year]) => parseInt(year) >= currentYear - 1 && parseInt(year) < currentYear + 3)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.projectedIntensity : getData(values)?.projectedEmissions,
            })),
          color: "#A6F334",
        },
      ];
      setChartData(formattedData);
    }
  }, [rawData, filters?.scope, method]);

  const { isFetching } = useGetSupplierOverviewData(years, filters?.categories, filters?.dataQuality, filters?.scope, {
    onError: (error) => {
      console.error("Error fetching supplier trends data:", error);
      showErrorToast(toast, "Failed to fetch supplier trends data");
    },
    onSuccess: (response) => {
      setRawData(response);
    },
    cacheTime: 60, // 1 minute
  });

  const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
    const dashedIds = ["Target", "Projected"];

    return series.map(({ id, data, color }, index) => (
      <path
        key={id}
        d={lineGenerator(
          data.map((d) => ({
            x: xScale(d.data.x),
            y: yScale(d.data.y),
          }))
        )}
        fill="none"
        stroke={color}
        style={
          dashedIds.includes(id)
            ? {
                strokeDasharray: "3, 6",
                strokeWidth: 3,
              }
            : {
                // simulate line with solid stroke
                strokeWidth: 1,
              }
        }
      />
    ));
  };

  return (
    <Box>

      <Accordion allowToggle>
        <AccordionItem>

          <AccordionButton>
            <H2 fontSize="body.2xl">Supplier Trends</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Flex direction="column" alignSelf="end" gap={2}>
              <Flex gap={2}>
                <YearStartEndDropdown
                  setStartYear={setStartYear}
                  startYear={startYear}
                  setEndYear={setEndYear}
                  endYear={endYear}
                />
                <SupplierFiltersCatQualityYear filters={filters} setFilters={setFilters} showFilters={["scope"]} />

                {/*<CategoryDropdown selected={categories} setSelected={setCategories} />*/}
                {/*<DataQualityDropdown selected={dataQuality} setSelected={setDataQuality} />*/}
                {/*<ScopeDropdown setSelected={setScope} selected={scope} />*/}
              </Flex>
              <Flex justifyContent="end">
                <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
              </Flex>
            </Flex>

            {isFetching && <LocalSpinner />}

            <Box height="500px" width="100%">
              {chartData && chartData.length > 0 && !isFetching && (
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 50, right: 50, bottom: 90, left: 120 }}
                  yScale={{ type: "linear", min: "auto", max: "auto", stacked: false, reverse: false }}
                  axisBottom={{ legend: "Time", legendOffset: 36 }}
                  axisLeft={{
                    legend: method === "Intensity" ? "Intensity (TCO2e/£m)" : "Total Emissions (TCO2e)",
                    legendOffset: -100,
                    legendPosition: "middle",
                  }}
                  enablePoints={true}
                  pointSize={10}
                  colors={(d) => d.color}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: "seriesColor" }}
                  pointLabelYOffset={-12}
                  enableTouchCrosshair={true}
                  useMesh={true}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      translateY: 60,
                      itemWidth: 80,
                      itemHeight: 22,
                      symbolShape: "circle",
                    },
                  ]}
                  layers={[
                    // includes all default layers
                    "grid",
                    "markers",
                    "axes",
                    "areas",
                    "crosshair",
                    "line",
                    "slices",
                    "points",
                    "mesh",
                    "legends",
                    DashedSolidLine, // Custom layer for dashed/solid lines
                  ]}
                  tooltip={({ point }) => (
                    <Box bg="white" p="8px" border="1px solid #ccc" borderRadius="4px">
                      <Text fontWeight="bold" color={point.color}>
                        {point.serieId}
                      </Text>
                      <Text>Year: {point.data.x}</Text>
                      <Text>Value: {getFormattedNumValue(Number(point.data.y), 2)}</Text>
                    </Box>
                  )}
                />
              )}
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

    </Box>
  );
};

export const YearStartEndDropdown = ({ setStartYear, startYear, setEndYear, endYear }) => {
  const years = Array.from({ length: 10 }, (_, i) => 2021 + i);

  return (
    <Flex gap={2}>
      <Menu>
        <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
          From: {startYear || "Select year"}
        </MenuButton>
        <MenuList>
          {years.map((year) => (
            <MenuItem key={year} onClick={() => setStartYear(year)} fontWeight={startYear === year ? "bold" : "normal"}>
              {year}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <Menu>
        <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
          To: {endYear || "Select year"}
        </MenuButton>
        <MenuList>
          {years.map((year) => (
            <MenuItem key={year} onClick={() => setEndYear(year)} fontWeight={endYear === year ? "bold" : "normal"}>
              {year}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Flex>
  );
};

export const ScopeDropdown = ({ selected, setSelected }) => {
  const options = ["All Scopes", "Scope 1 and 2", "Scope 3"];

  return (
    <Menu>
      <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
        {selected || "All Scopes"}
      </MenuButton>
      <MenuList>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => setSelected(option)}
            fontWeight={selected === option ? "bold" : "normal"}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export const TrendCalculationMethodDropdown = ({ selected, setSelected }) => {
  const options = ["Emission", "Intensity"];

  return (
    <Menu>
      <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
        Method: {selected}
      </MenuButton>
      <MenuList>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => setSelected(option)}
            fontWeight={selected === option ? "bold" : "normal"}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default SupplierTrends;
